import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api, ApiError } from "@/lib/api";
import { oneOf } from "@/lib/searchParams";
import { Panel } from "@/components/ui/Panel";
import { DataTable } from "@/components/ui/DataTable";
import { EmptyState } from "@/components/ui/EmptyState";
import { Pagination } from "@/components/ui/Pagination";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { useToast } from "@/features/toast/ToastProvider";
import { DealPickerDialog } from "@/components/DealPicker";
import { formatDate, formatMoney } from "@bookboost/shared/format";

interface InvoiceRow {
  id: string;
  stripe_invoice_id: string;
  deal_id: string | null;
  hubspot_deal_id: string | null;
  amount: number | null;
  amount_paid: number | null;
  currency: string;
  status: string;
  created_at_stripe: string | null;
  paid_at: string | null;
  commissionable: boolean | null;
  commissionable_reason: string | null;
  has_onboarding: boolean;
  deals: { deal_name: string | null; hubspot_deal_id: string } | null;
}

const invoicesSearch = z.object({
  q: z.string().optional(),
  status: z.string().optional(),
  linked: z.enum(["1", "0"]).optional(),
  page: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute("/admin/invoices")({
  validateSearch: invoicesSearch,
  component: InvoicesPage,
});

function InvoicesPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [linking, setLinking] = useState<string | null>(null); // invoice id being linked
  const [linkTarget, setLinkTarget] = useState<string | null>(null); // invoice id with the picker open

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "invoices", search],
    queryFn: () => api.get<{ invoices: InvoiceRow[]; total: number; page: number; pageSize: number }>("/api/admin/invoices", { searchParams: search }),
  });

  const link = useMutation({
    mutationFn: ({ id, hubspot_deal_id }: { id: string; hubspot_deal_id: string }) =>
      api.post(`/api/admin/invoices/${id}/link-deal`, { hubspot_deal_id }),
    onSuccess: () => {
      toast.ok("Linked.");
      setLinking(null);
      qc.invalidateQueries({ queryKey: ["admin", "invoices"] });
      qc.invalidateQueries({ queryKey: ["admin", "commissions"] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't link."),
  });

  function onPickDeal(hubspotDealId: string) {
    if (!linkTarget) return;
    const invoiceId = linkTarget;
    setLinkTarget(null);
    setLinking(invoiceId);
    link.mutate({ id: invoiceId, hubspot_deal_id: hubspotDealId });
  }

  return (
    <>
      <h1>Invoices</h1>

      <Panel>
        <form className="inline filters" style={{ gap: 12 }} onSubmit={(e) => e.preventDefault()}>
          <input
            type="search"
            placeholder="Search Stripe id / HubSpot deal id"
            value={search.q ?? ""}
            onChange={(e) => navigate({ search: { ...search, q: e.target.value || undefined, page: undefined } })}
            style={{ minWidth: 280, flex: 1 }}
          />
          <select
            value={search.status ?? ""}
            onChange={(e) => navigate({ search: { ...search, status: e.target.value || undefined, page: undefined } })}
          >
            <option value="">All status</option>
            {["draft", "open", "paid", "void", "uncollectible"].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            value={search.linked ?? ""}
            onChange={(e) =>
              navigate({ search: { ...search, linked: oneOf(e.target.value, ["1", "0"] as const), page: undefined } })
            }
          >
            <option value="">All linkage</option>
            <option value="1">Linked to deal</option>
            <option value="0">Orphans</option>
          </select>
          {Object.keys(search).length > 0 && (
            <button type="button" onClick={() => navigate({ search: {} })}>Reset</button>
          )}
        </form>
        <p className="muted" style={{ marginTop: 8, fontSize: 12 }}>
          {isLoading ? "Loading…" : `${(data?.total ?? 0).toLocaleString()} invoice${(data?.total ?? 0) === 1 ? "" : "s"}`}
        </p>
      </Panel>

      <Panel padding="tight">
        {!data || data.invoices.length === 0 ? (
          <EmptyState icon="invoices" title={isLoading ? "Loading…" : "No invoices match"} body="Try loosening filters." />
        ) : (
          <DataTable>
            <table className="table-cards">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Stripe</th>
                  <th>Deal</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th>Paid</th>
                  <th>Commissionable</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((inv) => (
                  <tr key={inv.id} style={!inv.deal_id ? { background: "color-mix(in srgb, var(--warn-soft) 30%, var(--surface))" } : undefined}>
                    <td data-label="Created" className="muted">{formatDate(inv.created_at_stripe)}</td>
                    <td data-label="Stripe"><code style={{ fontSize: 11 }}>{inv.stripe_invoice_id}</code></td>
                    <td data-label="Deal">
                      {inv.deals?.deal_name ?? (
                        <span className="muted" style={{ fontSize: 12 }}>
                          {inv.hubspot_deal_id ? `${inv.hubspot_deal_id} (not in DB)` : "orphan"}
                        </span>
                      )}
                    </td>
                    <td data-label="Status"><StatusBadge status={inv.status} /></td>
                    <td data-label="Amount">{formatMoney(inv.amount, inv.currency)}</td>
                    <td data-label="Paid">{formatMoney(inv.amount_paid, inv.currency)}</td>
                    <td data-label="Commissionable">
                      {inv.commissionable === true && <span className="badge earned">yes</span>}
                      {inv.commissionable === false && <span className="badge">no</span>}
                      {inv.commissionable === null && <span className="badge pending">review</span>}
                      {inv.has_onboarding && <span className="badge approved" style={{ marginLeft: 4 }}>★ onb</span>}
                    </td>
                    <td data-label="Actions">
                      {!inv.deal_id && (
                        <button
                          style={{ padding: "4px 10px", fontSize: 12 }}
                          disabled={linking === inv.id}
                          onClick={() => setLinkTarget(inv.id)}
                        >
                          {linking === inv.id ? "Linking…" : "Link to deal"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTable>
        )}
        {data && data.total > 0 && (
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPage={(p) => navigate({ search: { ...search, page: p } })}
          />
        )}
      </Panel>

      {linkTarget && (
        <DealPickerDialog
          title="Link invoice to deal"
          prompt="Link this invoice to a HubSpot deal? If it's a paid invoice, the commission worker will recompute."
          actionLabel="Link"
          onPick={onPickDeal}
          onClose={() => setLinkTarget(null)}
        />
      )}
    </>
  );
}
